import { type Ref, ref, watch } from "vue";

import type { Nullable } from "shared/types";

import { type Tab } from "./BaseTab.vue";

interface UseTabsKeyboardOptions {
  currentTab: Ref<Tab["name"]>;
  tablist: Readonly<Ref<HTMLElement | null>>;
  tabsList: Tab[];
}

// Maps a navigation key to the index of the enabled tab focus should move to.
type FocusMove = (bounds: { count: number; index: number }) => number;

const FOCUS_MOVES: Record<string, FocusMove> = {
  ArrowLeft: ({ count, index }) => (index - 1 + count) % count,
  ArrowRight: ({ count, index }) => (index + 1) % count,
  End: ({ count }) => count - 1,
  Home: () => 0,
};

/**
 * Manual-activation keyboard navigation for a tablist (WAI-ARIA tabs, manual activation).
 * See https://www.w3.org/WAI/ARIA/apg/patterns/tabs/examples/tabs-manual/
 * Manual activation means that arrow keys move focus but do not select a tab; selection is done with Enter or Space.
 */
export function useTabsKeyboard({
  currentTab,
  tablist,
  tabsList,
}: UseTabsKeyboardOptions) {
  // The tab focus currently rests on, tracked independently of the selected tab.
  const focusedName = ref<Nullable<Tab["name"]>>(null);

  const focusTab = (tab: Tab) => {
    focusedName.value = tab.name;
    tab.focus();
  };

  const onKeydown = (event: KeyboardEvent) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;

    const { target } = event;

    if (
      !(target instanceof HTMLElement) ||
      target.getAttribute("role") !== "tab"
    ) {
      return;
    }

    // Space selects a link tab (links don't activate on Space natively);
    // buttons and Enter already select without help.
    if (event.key === " ") {
      if (target instanceof HTMLAnchorElement) {
        event.preventDefault();
        target.click();
      }

      return;
    }

    const move = FOCUS_MOVES[event.key];

    if (!move) return;

    const enabledTabs = tabsList.filter((tab) => !tab.disabled());

    if (!enabledTabs.length) return;

    event.preventDefault();

    const currentIndex = enabledTabs.findIndex(
      ({ name }) => name === (focusedName.value ?? currentTab.value)
    );

    // Fall back to the first enabled tab when the focused tab is disabled or
    // gone, so wrap-around stays predictable.
    const index = currentIndex === -1 ? 0 : currentIndex;

    focusTab(enabledTabs[move({ count: enabledTabs.length, index })]);
  };

  const onFocusout = (event: FocusEvent) => {
    const nextTarget = event.relatedTarget;

    // Reset the roving focus target once focus leaves the tablist so keyboard
    // navigation resumes from the selected tab on re-entry.
    if (!(nextTarget instanceof Node) || !tablist.value?.contains(nextTarget)) {
      focusedName.value = null;
    }
  };

  // Resume keyboard navigation from the selected tab whenever selection changes.
  watch(currentTab, () => {
    focusedName.value = null;
  });

  return { focusedName, onFocusout, onKeydown };
}
